// What the global search shows for each kind of result (boss's task «ساخت نوار ناوبری»; docs/tasks/21-global-search.md):
// the order of the groups, an icon and colour per kind, the line under each title and where a click goes.

export const SEARCH_DEBOUNCE_MS = 300;
export const SEARCH_MIN_LENGTH = 2;

// group order of «همه نتایج»: messages, tasks, customers, then projects, goals and teams
export const SEARCH_TYPES = [
    {type: "message", icon: "forum", tone: "sky", label: "search.type.message", group: "search.group.message"},
    {type: "task", icon: "check_circle", tone: "emerald", label: "search.type.task", group: "search.group.task"},
    {type: "customer", icon: "domain", tone: "rose", label: "search.type.customer", group: "search.group.customer"},
    {type: "project", icon: "folder_open", tone: "indigo", label: "search.type.project", group: "search.group.project"},
    {type: "goal", icon: "ads_click", tone: "amber", label: "search.type.goal", group: "search.group.goal"},
    {type: "team", icon: "groups", tone: "teal", label: "search.type.team", group: "search.group.team"},
];
export const searchTypeMeta = (type) => SEARCH_TYPES.find((item) => item.type === type) || SEARCH_TYPES[0];

export const STATUS_LABELS = {
    task: {todo: "برای انجام", doing: "در حال انجام", blocked: "مسدود", done: "انجام‌شده"},
    project: {"on-way": "در مسیر", "has-risks": "در معرض ریسک", "out-of-way": "خارج از مسیر", achieved: "به‌دست‌آمده", canceled: "لغو شده", archived: "بیخیالش شدیم"},
    customer: {active: "فعال", inactive: "غیرفعال"},
};
STATUS_LABELS.goal = STATUS_LABELS.project;

export const statusLabel = (type, status) => (status ? STATUS_LABELS[type]?.[status] || status : "");

// the status choices of the /search page filter
export const STATUS_FILTERS = [
    ...Object.entries(STATUS_LABELS.task).map(([value, label]) => ({value, label: `${label} (کار)`, types: ["task"]})),
    ...Object.entries(STATUS_LABELS.project).map(([value, label]) => ({value, label: `${label} (پروژه / هدف)`, types: ["project", "goal"]})),
];

const personName = (item) => item?.person?.name || "";
const join = (parts) => parts.filter(Boolean).join(" • ");

// the line under a result's title, so similar titles can be told apart before clicking
export function contextLine(item, translate) {
    const t = (key, value) => `${translate(key)}: ${value}`;
    switch (item?.type) {
        case "task":
            return join([
                item.project?.title && t("search.context.project", item.project.title),
                personName(item) && t("search.context.assignee", personName(item)),
                !item.project && item.status && t("search.context.status", statusLabel("task", item.status)),
            ]);
        case "message":
            return join([
                personName(item) && t("search.context.sent-by", personName(item)),
                item.chat?.name && `${translate("search.context.in-chat")} ${item.chat.name}`,
            ]);
        case "project":
            return join([
                item.goal?.title && t("search.context.goal", item.goal.title),
                item.status && t("search.context.status", statusLabel("project", item.status)),
            ]);
        case "goal":
            return join([
                item.status && t("search.context.status", statusLabel("goal", item.status)),
                Number.isFinite(item.progress) && t("search.context.progress", `${item.progress}٪`),
                !item.status && personName(item) && t("search.context.owner", personName(item)),
            ]);
        case "team":
            return join([
                t("search.context.members", `${item.membersCount || 0} ${translate("search.context.people")}`),
                personName(item) && t("search.context.manager", personName(item)),
            ]);
        case "customer":
            return join([
                item.companyName && t("search.context.company", item.companyName),
                item.jobTitle,
                item.status && t("search.context.status", statusLabel("customer", item.status)),
            ]);
        default:
            return "";
    }
}

// Where a result opens. Tasks open their sidebar on top of the current page (the ?task= link every page handles).
export function resultTarget(item, location = {pathname: "/", search: ""}) {
    const key = item?.documentId || item?.id;
    switch (item?.type) {
        case "task": {
            const params = new URLSearchParams(location.search || "");
            params.set("task", key);
            return `${location.pathname || "/"}?${params.toString()}`;
        }
        case "project":
            return `/projects/${key}`;
        case "goal":
            return `/goals/${item.id}`;
        case "team":
            return `/teams/${key}`;
        case "customer":
            return `/customers/${key}`;
        case "message":
            return `/inbox?tab=conversations${item.chat?.documentId ? `&chat=${item.chat.documentId}` : ""}`;
        default:
            return null;
    }
}

// a title cut into plain and highlighted parts from the ranges the server returned
export function highlightParts(text, ranges) {
    const source = String(text || "");
    const parts = [];
    let cursor = 0;
    [...(ranges || [])]
        .filter((range) => Array.isArray(range) && range[1] > range[0])
        .sort((a, b) => a[0] - b[0])
        .forEach(([from, to]) => {
            const start = Math.max(from, cursor);
            const end = Math.min(to, source.length);
            if (end <= start) return;
            if (start > cursor) parts.push({text: source.slice(cursor, start), match: false});
            parts.push({text: source.slice(start, end), match: true});
            cursor = end;
        });
    if (cursor < source.length) parts.push({text: source.slice(cursor), match: false});
    return parts;
}

export const searchPageUrl = (query, type = "all") => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (type && type !== "all") params.set("type", type);
    return `/search?${params.toString()}`;
};
